import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Clock, MapPin } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';

const AUTO_SENTINEL = '__auto__';

// Common US zones + a "State default" sentinel that clears the override.
const OPTIONS = [
  { value: AUTO_SENTINEL, label: 'Use my address (auto-detect from state)' },
  { value: 'America/New_York', label: 'Eastern — New York, Miami, Atlanta' },
  { value: 'America/Chicago', label: 'Central — Chicago, Houston, Dallas' },
  { value: 'America/Denver', label: 'Mountain — Denver, Salt Lake City' },
  { value: 'America/Phoenix', label: 'Mountain (no DST) — Phoenix' },
  { value: 'America/Los_Angeles', label: 'Pacific — LA, Seattle, San Diego' },
  { value: 'America/Anchorage', label: 'Alaska — Anchorage' },
  { value: 'Pacific/Honolulu', label: 'Hawaii — Honolulu' },
  { value: 'America/Puerto_Rico', label: 'Atlantic — Puerto Rico, US Virgin Islands' },
];

/**
 * Compact settings card that lets a user override the timezone that
 * would otherwise be inferred from their agent-profile address.
 *
 * The backend does the state → timezone mapping automatically — this
 * card only matters for people who live in the minority zone of a
 * multi-zone state (e.g. the Florida Panhandle is Central even though
 * Florida is majority Eastern) or who prefer a specific label.
 */
export default function TimezonePreferenceCard() {
  const { user, refreshUser } = useAuth();
  const [selected, setSelected] = useState(AUTO_SENTINEL);
  const [saving, setSaving] = useState(false);
  const [nowPreview, setNowPreview] = useState('');

  const effectiveTz = user?.timezone || 'America/New_York';

  // Prefill: prefer the exact stored override so the picker reflects
  // what's actually saved. Fall back to the auto sentinel when the
  // user hasn't set an override yet (backend resolves via state).
  useEffect(() => {
    setSelected(user?.timezone || AUTO_SENTINEL);
  }, [user?.timezone]);

  // Live "now in your timezone" preview so the user can sanity-check
  // that their pick matches what their clock says.
  useEffect(() => {
    const render = () => {
      const tz = selected === AUTO_SENTINEL ? effectiveTz : selected;
      try {
        setNowPreview(
          new Intl.DateTimeFormat('en-US', {
            timeZone: tz,
            dateStyle: 'full',
            timeStyle: 'short',
          }).format(new Date())
        );
      } catch {
        setNowPreview('—');
      }
    };
    render();
    const t = setInterval(render, 30_000);
    return () => clearInterval(t);
  }, [selected, effectiveTz]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { timezone: selected === AUTO_SENTINEL ? '' : selected };
      await api.patch('/auth/me/timezone', payload);
      await refreshUser?.();
      toast.success(
        payload.timezone
          ? `Timezone locked to ${payload.timezone}. Dashboards will now render in your local clock.`
          : 'Timezone reverted to the value inferred from your agent address.'
      );
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not save timezone'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="timezone-preference-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Clock size={16} className="text-slate-600" />
          Timezone
        </CardTitle>
        <p className="text-[11px] text-slate-500 pt-1">
          By default the app reads the state on your agent profile and
          uses the majority timezone for that state (Florida → Eastern,
          California → Pacific, etc.). Change this only if you live in
          the minority zone of a multi-zone state.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
          <div className="flex items-center gap-2 text-slate-500">
            <MapPin size={12} /> Currently effective: <code className="text-slate-800">{effectiveTz}</code>
          </div>
          <div className="text-slate-600 mt-0.5 font-mono">{nowPreview}</div>
        </div>
        <Select value={selected} onValueChange={setSelected}>
          <SelectTrigger data-testid="timezone-preference-select">
            <SelectValue placeholder="Use my address (auto-detect from state)" />
          </SelectTrigger>
          <SelectContent>
            {OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex justify-end">
          <Button
            size="sm"
            onClick={save}
            disabled={saving || selected === (user?.timezone || AUTO_SENTINEL)}
            data-testid="timezone-preference-save"
          >
            {saving ? <Loader2 size={13} className="animate-spin mr-1" /> : null}
            Save timezone
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
